import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import Logo from "../components/Logo";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Logo showText linkTo="/" />
          
          <Link to="/">
            <Button variant="ghost" className="text-text-muted hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
          </Link>
        </div>
      </nav>

      <main className="pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-heading text-4xl font-bold tracking-tight mb-8">Terms of Service</h1>
          
          <div className="prose prose-invert max-w-none space-y-8 text-text-muted">
            <p className="text-lg">
              Last updated: January 20, 2026
            </p>

            {/* Company Information */}
            <section className="bg-surface border border-[#262626] rounded-lg p-6 -mx-2">
              <h2 className="text-xl font-heading font-semibold text-white mb-4">Company Information</h2>
              <p>
                ReplyDriven is operated by Reply Driven, a sole proprietorship registered in Iceland.
                Throughout these Terms, &quot;ReplyDriven&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot; refers to Reply Driven.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-heading font-semibold text-white mb-4">1. Acceptance of Terms</h2>
              <p>
                By accessing and using ReplyDriven, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-heading font-semibold text-white mb-4">2. Description of Service</h2>
              <p>
                ReplyDriven is an analytics service that connects to your Shopify store to provide weekly profit leak insights. We use AI to analyze your store data and deliver actionable recommendations.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-heading font-semibold text-white mb-4">3. Account Responsibilities</h2>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>You are responsible for maintaining the security of your account</li>
                <li>You must provide accurate information when connecting your Shopify store</li>
                <li>You are responsible for all activities that occur under your account</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-heading font-semibold text-white mb-4">4. Shopify Integration</h2>
              <p className="mb-4">By connecting your Shopify store, you authorize ReplyDriven to:</p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>Access read-only data including orders, customers, and products</li>
                <li>Analyze this data to generate insights</li>
                <li>Store analysis results for your reference</li>
              </ul>
              <p className="mt-4">
                We do not modify, delete, or create any data in your Shopify store.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-heading font-semibold text-white mb-4">5. AI-Generated Insights</h2>
              <p className="mb-3">
                Our insights are generated using artificial intelligence. While we strive for accuracy, these insights are recommendations and should be considered as one factor in your business decisions. We do not guarantee specific business outcomes.
              </p>
              <p className="text-text-muted text-sm">
                <strong>Important:</strong> AI-generated insights provided by ReplyDriven are for informational purposes only and do not constitute professional financial, legal, tax, or business advice. You should consult with qualified professionals before making significant business decisions based on our insights.
              </p>
            </section>

            {/* Billing, Trial, Cancellation & Refund Policy - Shopify Billing */}
            <section className="bg-surface border border-[#262626] rounded-lg p-6 -mx-2">
              <h2 className="text-xl font-heading font-semibold text-white mb-4">6. Billing, Trial, Cancellation & Refund Policy</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Trial</h3>
                  <p>
                    If your plan includes a free trial, your trial lasts 7 days. You will not be charged during the trial period. At the end of the trial, your subscription automatically begins and you will be billed the plan price shown at checkout unless you cancel before the trial ends.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Subscription Renewals</h3>
                  <p>
                    Subscriptions renew automatically on a 30-day billing cycle unless canceled. You can view your current plan and billing date at any time in your Shopify Admin.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Cancellation</h3>
                  <p className="mb-2">You can cancel your subscription at any time through your Shopify Admin:</p>
                  <ul className="list-disc list-inside space-y-1 pl-4">
                    <li>Go to Apps → ReplyDriven → Manage Subscription</li>
                    <li>Or navigate to Settings → Billing → Subscriptions in your Shopify Admin</li>
                  </ul>
                  <p className="mt-2">
                    Your subscription will remain active until the end of your current billing period. You will not be billed again after cancellation.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Refund Policy</h3>
                  <p className="mb-3">
                    Customers may request a full refund within 14 days of their initial payment.
                  </p>
                  <p className="mb-3">
                    Refund requests must be submitted within this 14-day period by contacting{" "}
                    <a href="mailto:hello@replydriven.com" className="text-[#D4F34A] hover:underline">hello@replydriven.com</a>.
                  </p>
                  <p className="mb-3">
                    Refunds requested after the 14-day period are not guaranteed.
                  </p>
                  <p>
                    All refunds are processed through Shopify's billing system and will be issued to the original payment method used at checkout.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Failed Payments</h3>
                  <p>
                    If a payment fails, Shopify may retry the charge automatically. Access to premium features may be restricted until payment is successfully processed.
                  </p>
                </div>

                <div className="pt-4 border-t border-[#262626]">
                  <p className="text-sm">
                    <strong className="text-white">Payment Processing:</strong> Subscriptions are managed through Shopify's billing system. Shopify handles all billing, payment processing, taxes, and invoices directly through your Shopify account.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-heading font-semibold text-white mb-4">7. Intellectual Property</h2>
              <p>
                The ReplyDriven service, including its design, features, and content, is owned by us and protected by intellectual property laws. Your Shopify data remains your property.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-heading font-semibold text-white mb-4">8. Limitation of Liability</h2>
              <p>
                ReplyDriven is provided &quot;as is&quot; without warranties of any kind. We are not liable for any indirect, incidental, or consequential damages arising from your use of the service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-heading font-semibold text-white mb-4">9. Termination</h2>
              <p>
                You may terminate your account at any time by deleting it from the Settings page. We reserve the right to terminate accounts that violate these terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-heading font-semibold text-white mb-4">10. Changes to Terms</h2>
              <p>
                We may modify these terms at any time. Continued use of the service after changes constitutes acceptance of the new terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-heading font-semibold text-white mb-4">11. Governing Law</h2>
              <p>
                These terms are governed by applicable laws. Any disputes shall be resolved through appropriate legal channels.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-heading font-semibold text-white mb-4">12. Contact</h2>
              <p>
                For questions about these Terms of Service or billing support, please contact us at{" "}
                <a href="mailto:hello@replydriven.com" className="text-[#D4F34A] hover:underline">
                  hello@replydriven.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 border-t border-[#262626]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Logo size="sm" showText linkTo="/" />
          
          <div className="flex items-center gap-6 text-sm text-text-muted">
            <a href="mailto:hello@replydriven.com" className="hover:text-white transition-colors">Contact</a>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
          </div>
          
          <p className="text-sm text-text-muted">
            © {new Date().getFullYear()} ReplyDriven. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
